import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { X, ChevronDown, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { stringSimilarity } from "@/lib/string-similarity";

export interface TokenComboboxOption {
  label: string;
  group?: string;
}

export interface TokenComboboxGroup {
  key: string;
  label: string;
}

interface TokenComboboxProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: TokenComboboxOption[];
  /** When provided (and non-empty), options are rendered under group headers in this order. Omit for a flat list. */
  groups?: TokenComboboxGroup[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  /** Similarity (0-1) above which the top existing option is treated as a "strong suggestion" that Enter selects. */
  strongMatchThreshold?: number;
  listAriaLabel?: string;
  /** Unique prefix for aria/list ids so multiple instances don't collide. */
  idPrefix?: string;
}

// INVARIANT: the option list opens only on explicit in-component
// intent — a click on the widget, a keystroke that changes the
// input, or ArrowUp/ArrowDown while closed. Do NOT add an onFocus
// handler that opens the list. The list is an overlay that covers
// the form fields below it, and any click landing on it selects an
// option, so a list opened without the user's awareness converts
// their next click into an unintended selection. Browser-level
// focus restore (returning from another application or browser
// tab) fires focus with no user intent, which is how open-on-focus
// caused exactly that bug.
export default function TokenCombobox({
  value,
  onChange,
  options,
  groups,
  placeholder = "Search or add...",
  disabled = false,
  isLoading = false,
  loadingLabel = "Loading...",
  emptyLabel = "No matches found",
  strongMatchThreshold = 0.7,
  listAriaLabel = "Options",
  idPrefix = "token-combobox",
}: TokenComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const trimmedSearch = search.trim();
  const q = trimmedSearch.toLowerCase();

  // Substring matches drive the visible list.
  const substringMatches = useMemo(() => {
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, q]);

  // Strongest fuzzy match across ALL options (not just substring hits), so typos like
  // "Los Angles" can still surface "Los Angeles" and avoid near-duplicate entries.
  const strongMatch = useMemo(() => {
    if (!q) return null;
    let best: TokenComboboxOption | null = null;
    let bestScore = 0;
    for (const o of options) {
      const score = stringSimilarity(q, o.label.toLowerCase());
      if (score > bestScore) {
        bestScore = score;
        best = o;
      }
    }
    return best && bestScore >= strongMatchThreshold ? best : null;
  }, [options, q, strongMatchThreshold]);

  // Visible list = substring matches, plus the strong fuzzy match when it isn't already shown.
  const displayOptions = useMemo(() => {
    if (!q) return options;
    if (strongMatch && !substringMatches.some((o) => o.label === strongMatch.label)) {
      return [strongMatch, ...substringMatches];
    }
    return substringMatches;
  }, [q, options, substringMatches, strongMatch]);

  // Flatten in group order (when grouped) so keyboard navigation matches display order.
  const flatOptions = useMemo(() => {
    if (!groups || groups.length === 0) return displayOptions;
    const ordered: TokenComboboxOption[] = [];
    for (const g of groups) {
      for (const o of displayOptions) {
        if (o.group === g.key) ordered.push(o);
      }
    }
    // Include any option whose group isn't in the provided groups list (defensive).
    for (const o of displayOptions) {
      if (!groups.some((g) => g.key === o.group)) ordered.push(o);
    }
    return ordered;
  }, [displayOptions, groups]);

  const hasExactMatch = useMemo(
    () => options.some((o) => o.label.toLowerCase() === q),
    [options, q],
  );
  const canAddCustom = trimmedSearch.length > 0 && !hasExactMatch;
  const ADD_INDEX = flatOptions.length;

  // Index of the strongest existing match for the current query, or -1 when none is strong.
  const strongMatchIndex = useMemo(() => {
    if (!strongMatch) return -1;
    return flatOptions.findIndex((o) => o.label === strongMatch.label);
  }, [flatOptions, strongMatch]);

  // Default highlight: strong suggestion when one exists, otherwise the "Add" row for new entries.
  const defaultActiveIndex = useMemo(() => {
    if (!trimmedSearch) return -1;
    if (strongMatchIndex >= 0) return strongMatchIndex;
    if (canAddCustom) return ADD_INDEX;
    return flatOptions.length > 0 ? 0 : -1;
  }, [trimmedSearch, strongMatchIndex, canAddCustom, ADD_INDEX, flatOptions.length]);

  useEffect(() => {
    setActiveIndex(defaultActiveIndex);
  }, [defaultActiveIndex]);

  const addCustom = useCallback(() => {
    if (!trimmedSearch) return;
    if (!value.includes(trimmedSearch)) {
      onChange([...value, trimmedSearch]);
    }
    setSearch("");
    setOpen(false);
    inputRef.current?.focus();
  }, [trimmedSearch, value, onChange]);

  const toggle = useCallback(
    (label: string) => {
      if (value.includes(label)) {
        onChange(value.filter((v) => v !== label));
      } else {
        onChange([...value, label]);
      }
    },
    [value, onChange],
  );

  const remove = (label: string) => {
    onChange(value.filter((v) => v !== label));
  };

  const scrollActiveIntoView = (index: number) => {
    if (index === ADD_INDEX) {
      addButtonRef.current?.scrollIntoView({ block: "nearest" });
      return;
    }
    const el = optionRefs.current[index];
    if (el) el.scrollIntoView({ block: "nearest" });
  };

  const totalOptions = flatOptions.length + (canAddCustom ? 1 : 0);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setOpen(true);
        return;
      }
      if (e.key === "Enter") {
        // No-op while the list is closed: a stale activeIndex must never
        // toggle a tag against the unfiltered list. Still preventDefault so
        // Enter doesn't submit an enclosing form.
        e.preventDefault();
        return;
      }
    }

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        if (totalOptions === 0) break;
        const next = activeIndex < totalOptions - 1 ? activeIndex + 1 : 0;
        setActiveIndex(next);
        scrollActiveIntoView(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        if (totalOptions === 0) break;
        const prev = activeIndex > 0 ? activeIndex - 1 : totalOptions - 1;
        setActiveIndex(prev);
        scrollActiveIntoView(prev);
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex === ADD_INDEX && canAddCustom) {
          addCustom();
        } else if (activeIndex >= 0 && activeIndex < flatOptions.length) {
          toggle(flatOptions[activeIndex].label);
          setSearch("");
          setOpen(false);
        } else if (canAddCustom) {
          addCustom();
        }
        break;
      }
      case "Escape": {
        setOpen(false);
        setSearch("");
        inputRef.current?.blur();
        break;
      }
      case "Backspace": {
        if (search === "" && value.length > 0) {
          onChange(value.slice(0, -1));
        }
        break;
      }
    }
  };

  const renderOption = (option: TokenComboboxOption, idx: number) => {
    const selected = value.includes(option.label);
    const isActive = activeIndex === idx;
    return (
      <button
        key={option.label}
        id={`${idPrefix}-opt-${idx}`}
        ref={(el) => {
          optionRefs.current[idx] = el;
        }}
        type="button"
        role="option"
        aria-selected={selected}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          toggle(option.label);
          setSearch("");
          setActiveIndex(idx);
          setOpen(false);
        }}
        onMouseEnter={() => setActiveIndex(idx)}
        className={`w-full text-left px-3 py-2 text-sm font-sans flex items-center justify-between transition-colors ${
          isActive
            ? selected
              ? "bg-primary/20 text-primary"
              : "bg-zinc-800 text-white"
            : selected
              ? "bg-primary/10 text-primary"
              : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
        }`}
      >
        <span>{option.label}</span>
        {selected && (
          <span className="text-[10px] text-primary font-semibold shrink-0 ml-2">Selected</span>
        )}
      </button>
    );
  };

  const renderGrouped = () => {
    if (!groups || groups.length === 0) {
      return flatOptions.map((o, i) => renderOption(o, i));
    }
    let cursor = 0;
    return groups.map((g) => {
      const groupOptions = flatOptions.filter((o) => o.group === g.key);
      if (groupOptions.length === 0) return null;
      const base = cursor;
      cursor += groupOptions.length;
      return (
        <div key={g.key}>
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 sticky top-0 bg-zinc-900/95">
            {g.label}
          </div>
          {groupOptions.map((o, i) => renderOption(o, base + i))}
        </div>
      );
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`min-h-[36px] flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-md border bg-zinc-800 border-primary/20 cursor-text transition-colors ${
          open ? "border-primary/50" : "hover:border-primary/40"
        } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        {value.map((label) => (
          <Badge
            key={label}
            variant="secondary"
            className="bg-zinc-700 text-zinc-200 border border-white/10 font-sans text-xs px-2 py-0.5 flex items-center gap-1 cursor-default"
          >
            {label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(label);
              }}
              className="text-zinc-400 hover:text-white transition-colors ml-0.5"
              aria-label={`Remove ${label}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </Badge>
        ))}
        <div className="flex-1 flex items-center gap-1 min-w-[120px]">
          <input
            ref={inputRef}
            type="text"
            role="searchbox"
            aria-autocomplete="list"
            aria-controls={`${idPrefix}-listbox`}
            aria-activedescendant={
              activeIndex >= 0 && activeIndex < flatOptions.length
                ? `${idPrefix}-opt-${activeIndex}`
                : undefined
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ""}
            className="flex-1 bg-transparent text-white text-sm placeholder:text-zinc-500 outline-none font-sans min-w-[80px]"
            disabled={disabled}
            autoComplete="off"
          />
          <ChevronDown
            className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {open && (
        <div
          ref={listRef}
          id={`${idPrefix}-listbox`}
          role="listbox"
          aria-multiselectable="true"
          aria-label={listAriaLabel}
          className="absolute z-50 w-full mt-1 bg-zinc-900 border border-white/10 rounded-md shadow-2xl shadow-black/60 max-h-64 overflow-y-auto"
        >
          {isLoading ? (
            <div className="px-3 py-4 text-sm text-zinc-500 font-sans text-center">{loadingLabel}</div>
          ) : (
            <>
              {flatOptions.length > 0 && renderGrouped()}
              {flatOptions.length === 0 && !canAddCustom && (
                <div className="px-3 py-4 text-sm text-zinc-500 font-sans text-center">{emptyLabel}</div>
              )}
              {canAddCustom && (
                <div className={flatOptions.length > 0 ? "border-t border-white/10" : ""}>
                  <button
                    ref={addButtonRef}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === ADD_INDEX}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      addCustom();
                    }}
                    onMouseEnter={() => setActiveIndex(ADD_INDEX)}
                    className={`w-full text-left px-3 py-2 text-sm font-sans flex items-center gap-2 transition-colors ${
                      activeIndex === ADD_INDEX
                        ? "bg-zinc-800 text-white"
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5 shrink-0 text-[#E8184A]" />
                    <span>Add &ldquo;{trimmedSearch}&rdquo;</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
